import { Puzzle, MessagesSquare, Compass, HeartHandshake, Route, Stethoscope } from "lucide-react";
import { ChapterHeading, Reveal } from "./Reveal";

const PILLARS = [
  {
    icon: Puzzle,
    title: "Individualized ABA",
    text: "Function-based treatment plans centered on each child's strengths and needs.",
    featured: true,
  },
  {
    icon: MessagesSquare,
    title: "Communication",
    text: "Meaningful communication skills for daily life, relationships, and participation.",
  },
  {
    icon: Compass,
    title: "Independence",
    text: "Practical goals designed to build confidence, daily living skills, and independence.",
  },
  {
    icon: HeartHandshake,
    title: "Family Collaboration",
    text: "Parents and caregivers are active partners throughout care.",
  },
  {
    icon: Route,
    title: "Generalization",
    text: "Skills practiced across home, school, clinic, and community settings when appropriate.",
  },
  {
    icon: Stethoscope,
    title: "Team-Based Care",
    text: "Collaboration with physicians, educators, SLPs, OTs, and other professionals.",
  },
];

const FOUNDER_IMG =
  "https://images.unsplash.com/photo-1596066190600-3af9aadaaea1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwxfHxjaGlsZCUyMHRoZXJhcGlzdCUyMHBsYXlpbmd8ZW58MHx8fHwxNzg2NzE3NDIxfDA&ixlib=rb-4.1.0&q=85";

export const About = () => (
  <section id="about" data-testid="about-section" className="py-24 md:py-32">
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <ChapterHeading
        number="01"
        eyebrow="About Us"
        title="Driven by people."
        accent="Defined by progress."
        testid="about-heading"
      />

      <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 mb-20">
        <Reveal className="lg:col-span-7">
          <p className="text-base md:text-lg leading-relaxed text-brand-muted max-w-2xl">
            Milestone Pediatric Therapy is being developed by{" "}
            <span className="text-brand-ink font-semibold">Nora Ben-Achour, BA, M.Ed.</span>, who brings
            approximately 15 years of experience working with children with autism and other
            developmental and learning needs within educational settings. Her experience includes
            evaluating students, identifying individual learning, communication, behavioral, and
            functional needs, collaborating with families and multidisciplinary teams, and developing
            individualized plans designed to help children make meaningful progress at school and
            beyond.
          </p>
          <p className="mt-6 text-base md:text-lg leading-relaxed text-brand-muted max-w-2xl">
            At Milestone Pediatric Therapy, our team includes highly qualified{" "}
            <span className="text-brand-ink font-semibold">Board Certified Behavior Analysts (BCBAs)</span>{" "}
            who provide individualized, evidence-based ABA therapy. Our understanding of both ABA and
            the educational environment allows us to focus on skills that truly matter in a child's
            everyday life — from functional communication, social interaction, behavioral and
            emotional regulation, and daily living skills to classroom readiness, transitions,
            following routines, and greater independence.
          </p>
          <p className="mt-6 text-base md:text-lg leading-relaxed text-brand-muted max-w-2xl">
            We also collaborate with{" "}
            <span className="text-brand-ink font-semibold">Speech-Language Pathologists (SLPs)</span> and{" "}
            <span className="text-brand-ink font-semibold">Occupational Therapists (OTs)</span> to
            support a more comprehensive approach to each child's care. By working across
            disciplines, we can better address communication, sensory, motor, behavioral, social,
            and functional needs while helping ensure that strategies are coordinated and meaningful
            across home, school, clinic, and community settings.
          </p>
          <p className="mt-6 text-base md:text-lg leading-relaxed text-brand-muted max-w-2xl">
            Our goal is not to keep a child in therapy longer than necessary. We focus on measurable,
            meaningful progress and helping each child develop skills that carry over into everyday
            life. We want our kiddos to transition into school with greater confidence and continue
            building the communication, independence, and functional skills they need to thrive
            throughout their journey.
          </p>
        </Reveal>
        <Reveal delay={0.15} className="lg:col-span-5">
          <div className="relative">
            <div className="absolute -bottom-4 -right-4 h-full w-full rounded-[2rem] bg-brand-pale" aria-hidden="true" />
            <img
              src={FOUNDER_IMG}
              alt="Child engaged in a playful therapy activity"
              data-testid="about-image"
              className="relative h-64 md:h-72 w-full object-cover rounded-[2rem] shadow-soft"
            />
          </div>
        </Reveal>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="about-pillars-grid">
        {PILLARS.map((p, i) => (
          <Reveal key={p.title} delay={0.08 * i}>
            <div
              data-testid={`pillar-${p.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={`group h-full rounded-3xl border p-8 transition-[transform,box-shadow,background-color,border-color] duration-500 hover:-translate-y-1.5 hover:shadow-lift ${
                p.featured
                  ? "bg-brand-blue text-white border-brand-blue"
                  : "bg-white border-brand-lightblue/40 shadow-soft hover:border-brand-lightblue"
              }`}
            >
              <span
                className={`flex h-12 w-12 items-center justify-center rounded-2xl mb-6 transition-transform duration-500 group-hover:scale-110 ${
                  p.featured ? "bg-white/15 text-brand-yellow" : "bg-brand-pale text-brand-blue"
                }`}
              >
                <p.icon className="h-6 w-6" />
              </span>
              <h3 className={`font-serif text-2xl font-semibold mb-3 ${p.featured ? "text-white" : "text-brand-blue"}`}>
                {p.title}
              </h3>
              <p className={`text-sm md:text-base leading-relaxed ${p.featured ? "text-white/85" : "text-brand-muted"}`}>
                {p.text}
              </p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);
